import overSome from 'lodash/fp/overSome';
import upperFirst from 'lodash/fp/upperFirst';
import styles from './Element.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';

export const borderRadiusSizes = [
  'zero',
  'xs',
  'sm',
  'md',
  'lg',
  'rounded',
] as const;

type BorderRadiusSize = ArrayElementType<typeof borderRadiusSizes>;

type BorderRadiusOptions = {
  size: BorderRadiusSize;
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
};
// Alias for backwards compatibility
export type BorderRadius = BorderRadiusOptions;

export type BorderRadiusProps = true | Partial<BorderRadiusOptions>;

const getOptions = (input: BorderRadiusProps): BorderRadiusOptions => {
  const defaultSize = 'sm';

  if (input === true) {
    return {
      left: true,
      right: true,
      top: true,
      bottom: true,
      size: defaultSize,
    };
  } else {
    const defaultDirectionValue = !(
      input.left ||
      input.right ||
      input.top ||
      input.bottom
    );

    return {
      left: input.left ?? defaultDirectionValue,
      right: input.right ?? defaultDirectionValue,
      top: input.top ?? defaultDirectionValue,
      bottom: input.bottom ?? defaultDirectionValue,
      size: input.size ?? defaultSize,
    };
  }
};

export default (input: BorderRadiusProps) => {
  const options = getOptions(input);

  const radiusSize = upperFirst(options.size);

  return {
    [styles[`element_borderRadius__topLeft${radiusSize}`]]: hasTopLeftRadius(
      options
    ),
    [styles[`element_borderRadius__topRight${radiusSize}`]]: hasTopRightRadius(
      options
    ),
    [styles[
      `element_borderRadius__bottomRight${radiusSize}`
    ]]: hasBottomRightRadius(options),
    [styles[
      `element_borderRadius__bottomLeft${radiusSize}`
    ]]: hasBottomLeftRadius(options),
  };
};

const hasTopLeftRadius = overSome<BorderRadiusOptions>([
  ({ left, right, top }) => top && !left && !right,
  ({ left, top, bottom }) => !top && left && !bottom,
  ({ left, top }) => top && left,
]);

const hasTopRightRadius = overSome<BorderRadiusOptions>([
  ({ left, right, top }) => top && !right && !left,
  ({ right, top, bottom }) => !top && right && !bottom,
  ({ right, top }) => top && right,
]);

const hasBottomLeftRadius = overSome<BorderRadiusOptions>([
  ({ left, right, bottom }) => bottom && !left && !right,
  ({ left, top, bottom }) => !bottom && left && !top,
  ({ left, bottom }) => bottom && left,
]);

const hasBottomRightRadius = overSome<BorderRadiusOptions>([
  ({ left, right, bottom }) => bottom && !right && !left,
  ({ right, top, bottom }) => !bottom && right && !top,
  ({ right, bottom }) => bottom && right,
]);
